import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Phone } from "lucide-react";

import { BusinessChecklistGate } from "@/components/business-checklist-gate";
import { PageHero } from "@/components/page-parts";
import { FIRM, localBusinessSchema } from "@/lib/firm";
import { GUIDES } from "@/lib/guides";

const CHECKLIST = {
  href: "/business-sale-purchase-checklist.pdf",
  label: "Business Sale & Purchase Checklist (PDF)",
  note: "Two pages of the documents to request and the checks to complete before you sign and before you settle — financials, lease, employees, PPSR, licences and settlement day.",
};

const RELATED_SLUGS = [
  "buying-or-selling-a-business-in-queensland",
  "business-sale-step-by-step-queensland",
  "buying-and-selling-body-corporate-management-rights",
  "commercial-property-conveyancing-queensland",
] as const;

const FAQS = [
  {
    q: "What is in the business sale and purchase checklist?",
    a: "Two pages covering the documents to request from the other side and the checks to complete at each stage: three years of financial statements, BAS and tax returns, the lease and landlord consent, employee records and entitlement balances, plant and equipment with finance details, PPSR searches, licences and permits, supplier and customer contracts, and the settlement-day items such as releases, transfers, stocktake and passwords.",
  },
  {
    q: "Is the checklist free?",
    a: "Yes. Give us your name, email and a few details about your deal and the PDF unlocks straight away. We also email you a copy along with the next steps for the stage your deal is at. There is no cost and no obligation.",
  },
  {
    q: "Does it apply to Queensland business sales specifically?",
    a: "Yes. It is written for Queensland asset sales — Queensland retail shop lease assignment requirements, Queensland licensing and duty position, and the PPSR and GST going-concern issues that apply nationally.",
  },
  {
    q: "Should a buyer or a seller use it?",
    a: "Both. A buyer uses it as the document request list and due diligence plan. A seller uses it to assemble the pack before going to market, which shortens due diligence and reduces the price chipping that comes from missing paperwork.",
  },
  {
    q: "When should I use the checklist?",
    a: "Before you sign anything if you can, and no later than the first day of the due diligence period. Landlord consent and licence transfers are the items that delay settlement, so the earlier the requests go out, the better.",
  },
  {
    q: "Do I still need a lawyer if I work through the checklist?",
    a: "The checklist tells you what to ask for; it does not tell you what the answers mean or how the contract should respond to them. Business contracts are not standardised the way residential conveyancing is, and the risk sits in the lease, the warranties, the restraint and the settlement mechanics. Have the contract reviewed before you sign, or keep it conditional so it can be.",
  },
  {
    q: "What does Coastside Law charge to act on a business sale?",
    a: "We quote a fixed fee once we have seen the contract and know whether a lease assignment, licence transfer or franchise consent is involved. The first conversation is obligation free — call us on " + FIRM.phone + ".",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const webPageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: "Business sale and purchase checklist (Queensland)",
  description:
    "Free downloadable checklist of the documents to request and the checks to complete when buying or selling a business in Queensland.",
  isPartOf: { "@type": "WebSite", name: FIRM.name },
  mainEntity: {
    "@type": "DigitalDocument",
    name: CHECKLIST.label,
    fileFormat: "application/pdf",
    url: `https://coastsidelaw.com.au${CHECKLIST.href}`,
    about: "Buying or selling a business in Queensland",
    publisher: { "@id": "https://coastsidelaw.com.au/#legalservice" },
  },
};

export const Route = createFileRoute("/business-sale-checklist")({
  head: () => ({
    meta: [
      { title: fitTitle(`Business Sale Checklist (QLD) — Free Download | ${FIRM.name}`) },
      {
        name: "description",
        content:
          fitDescription("Free Queensland business sale and purchase checklist: the documents to request, due diligence checks, lease assignment steps and settlement-day items for buyers and sellers."),
      },
      { property: "og:title", content: fitTitle(`Business sale & purchase checklist (QLD) | ${FIRM.name}`) },
      {
        property: "og:description",
        content:
          fitDescription("Download the two-page checklist of documents to request and checks to complete before you sign and before you settle."),
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "/business-sale-checklist" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "/business-sale-checklist" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(localBusinessSchema) },
      { type: "application/ld+json", children: JSON.stringify(webPageSchema) },
      { type: "application/ld+json", children: JSON.stringify(faqSchema) },
    ],
  }),
  component: BusinessSaleChecklistPage,
});

function BusinessSaleChecklistPage() {
  const related = RELATED_SLUGS.map((slug) => GUIDES.find((g) => g.slug === slug)).filter(
    (g): g is NonNullable<typeof g> => Boolean(g),
  );

  return (
    <>
      <PageHero
        eyebrow="Free download"
        title="Business sale & purchase checklist"
        lead="The documents to request and the checks to complete when you buy or sell a business in Queensland — from the first offer through due diligence, landlord consent and settlement day."
      />

      <section className="mx-auto max-w-3xl px-5 py-14">
        <BusinessChecklistGate
          href={CHECKLIST.href}
          label={CHECKLIST.label}
          note={CHECKLIST.note}
          guideTitle="Business sale checklist download page"
        />

        <h2 className="font-serif text-2xl text-foreground">What the checklist covers</h2>
        <ul className="mt-5 space-y-3 text-sm leading-relaxed text-muted-foreground">
          {[
            "Financials: three years of profit and loss, balance sheets, BAS and tax returns, reconciled to the bank statements",
            "Lease: term, options, rent reviews, outgoings, make-good and the landlord consent process",
            "Employees: entitlement balances, awards and contracts, and who pays out leave at settlement",
            "Plant and equipment: what is owned, what is financed, and the PPSR searches and releases",
            "Licences and permits: liquor, food, trade and council approvals, and whether they transfer",
            "Settlement day: stocktake, adjustments, transfers, releases, keys, passwords and online accounts",
          ].map((item) => (
            <li key={item} className="flex gap-3">
              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
              <span>{item}</span>
            </li>
          ))}
        </ul>

        <h2 className="mt-14 font-serif text-2xl text-foreground">
          Business sale checklist FAQs
        </h2>
        <dl className="mt-6 space-y-6">
          {FAQS.map((faq) => (
            <div key={faq.q} className="rounded-sm border border-border bg-card p-5">
              <dt className="font-serif text-lg leading-snug text-foreground">{faq.q}</dt>
              <dd className="mt-2 text-sm leading-relaxed text-muted-foreground">{faq.a}</dd>
            </div>
          ))}
        </dl>

        <h2 className="mt-14 font-serif text-2xl text-foreground">Keep reading</h2>
        <ul className="mt-5 grid gap-4 sm:grid-cols-2">
          {related.map((guide) => (
            <li key={guide.slug}>
              <Link
                to="/guides/$slug"
                params={{ slug: guide.slug }}
                className="group flex h-full flex-col rounded-sm border border-border bg-card p-5 transition-colors hover:border-primary"
              >
                <span className="font-serif text-lg leading-snug text-foreground group-hover:text-primary">
                  {guide.title}
                </span>
                <span className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
                  Read the guide
                  <ArrowRight className="size-4" aria-hidden="true" />
                </span>
              </Link>
            </li>
          ))}
        </ul>

        <div className="mt-14 rounded-sm border border-border bg-secondary/40 p-6">
          <h2 className="font-serif text-2xl text-foreground">Talk it through before you sign</h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Send us the contract and we will quote a fixed fee before doing any work. The first
            conversation is obligation free.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center rounded-sm bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
            >
              Request a consultation
            </Link>
            <a
              href={FIRM.phoneHref}
              className="inline-flex items-center gap-2 rounded-sm border border-border px-6 py-3 text-sm font-semibold text-foreground transition-colors hover:bg-secondary"
            >
              <Phone className="size-4 text-primary" aria-hidden="true" />
              Call {FIRM.phone}
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
